import { useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';

import {
  Assignment,
  convertAssignmentIntoAssignmentPayload,
  useEditAssignmentMutation,
} from 'shared/api/assignment-service';
import { useLazyGetCodeInfoQuery } from 'shared/api/free-to-paid-bff';
import { ASSIGNMENT_UPDATED_EVENT, RealtimeService } from 'shared/lib/realtime';
import { Tooltip } from 'shared/ui/tooltip';
import { useAppDispatch } from 'shared/lib/redux';
import {
  isSSCAssignment,
  useEscapeKey,
  useOnClickOutside,
  useOnFocusOutside,
} from 'shared/lib/utils';
import {
  CODE_INFO_ERROR_MESSAGE,
  ROTATE_X_ANIMATION_DURATION,
} from 'shared/config/constants';
import { XApiEventKeys } from 'shared/api/event-service';
import {
  setEditingAssignment,
  setIsAssignmentActionNotificationActive,
  setIsAssignmentPanelOpened,
  setAssignmentActionErrorMessage,
  getAssignmentsErrorMessages,
  useAssignmentActionTimestampHandlers,
} from 'features/assignment-panel';

import { AssignmentActionsMenuOption } from '../assignment-actions-menu-option';

import styles from './styles.module.scss';

export type AssignmentActionsMenuProps = {
  assignment: Assignment;
  onAssignmentActionsXApiEvents?: (
    eventType: XApiEventKeys,
    isRightClick?: boolean,
  ) => void;
  onShowExpirationDateClick?: (expirationTimestamp: string) => void;
};

export const AssignmentActionsMenu = ({
  assignment,
  onAssignmentActionsXApiEvents,
  onShowExpirationDateClick,
}: AssignmentActionsMenuProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const ASSIGNMENTS_ERROR_MESSAGE = getAssignmentsErrorMessages(t);

  const { writeAssignmentActionTimestamp } =
    useAssignmentActionTimestampHandlers();

  const assignmentActionsMenuRef = useRef<HTMLDivElement>(null);
  const assignmentActionsDropdownRef = useRef<HTMLDivElement>(null);

  const [isAssignmentActionsDropdownOpen, setIsAssignmentActionsDropdownOpen] =
    useState(false);
  const [isExpirationDateVisible, setIsExpirationDateVisible] = useState(false);

  const [editAssignment] = useEditAssignmentMutation();
  const [fetchCodeInfo, { data: codeInfo, isLoading: isCodeInfoLoading }] =
    useLazyGetCodeInfoQuery();

  const isAssignmentUnassigned = Boolean(assignment.archived);
  const isSingleSessionCodeAssignment = isSSCAssignment(assignment);

  const classCode = isSingleSessionCodeAssignment
    ? assignment.content.classCode
    : '';
  const expirationDateOptionName = isExpirationDateVisible
    ? t('Hide Expiration Date')
    : t('Show Expiration Date');
  const handleMenuClose = () => {
    if (!isAssignmentActionsDropdownOpen) return;

    setIsAssignmentActionsDropdownOpen(false);
  };

  useOnClickOutside(assignmentActionsMenuRef, handleMenuClose);
  useOnFocusOutside(assignmentActionsMenuRef, handleMenuClose);
  useEscapeKey(handleMenuClose);

  const handleAssignmentBtnClick = () => {
    setIsAssignmentActionsDropdownOpen(!isAssignmentActionsDropdownOpen);
  };

  const handleEditClick = () => {
    onAssignmentActionsXApiEvents?.(XApiEventKeys.assignmentEditSelected);

    dispatch(setEditingAssignment(assignment));
    dispatch(setIsAssignmentPanelOpened(true));
    setIsAssignmentActionsDropdownOpen(false);
  };

  const editArchivedStatus = (newArchiveStatus: boolean) => {
    // Store assignment data of the assignment that's being unassigned or reassigned (for the edit request to undo).
    dispatch(setEditingAssignment(assignment));

    setIsAssignmentActionsDropdownOpen(false);

    const assignmentPayload = convertAssignmentIntoAssignmentPayload(
      assignment,
      {
        archived: newArchiveStatus,
      },
    );

    // reset error message before each request to apply correct styles to notification popup
    dispatch(setAssignmentActionErrorMessage(null));

    editAssignment(assignmentPayload)
      .unwrap()
      .then(() => {
        RealtimeService.sendRealtimeEvent(
          assignment.classBks[0],
          ASSIGNMENT_UPDATED_EVENT,
          {
            assignment,
            startDate: assignment.startDate,
            dueDate: assignment.dueDate,
            isDeleted: newArchiveStatus,
          },
        );
        writeAssignmentActionTimestamp();
      })
      .catch(() => {
        const errorMessage = newArchiveStatus
          ? ASSIGNMENTS_ERROR_MESSAGE.UNASSIGN
          : ASSIGNMENTS_ERROR_MESSAGE.REASSIGN;

        dispatch(setAssignmentActionErrorMessage(errorMessage));
      })
      .finally(() => {
        // Show "Undo assignment action" notification.
        dispatch(setIsAssignmentActionNotificationActive(true));
      });
  };

  const handleEditContextMenu = () =>
    onAssignmentActionsXApiEvents?.(XApiEventKeys.assignmentEditSelected, true);

  const handleUnassignClick = () => {
    editArchivedStatus(true);

    onAssignmentActionsXApiEvents?.(XApiEventKeys.assignmentUnassignSelected);
  };

  const handleUnassignContextMenu = () =>
    onAssignmentActionsXApiEvents?.(
      XApiEventKeys.assignmentUnassignSelected,
      true,
    );

  const handleReassignClick = () => {
    editArchivedStatus(false);

    onAssignmentActionsXApiEvents?.(XApiEventKeys.assignmentReassignSelected);

    // Show "Undo assignment action" notification.
    dispatch(setIsAssignmentActionNotificationActive(true));
  };

  const handleReassignContextMenu = () =>
    onAssignmentActionsXApiEvents?.(
      XApiEventKeys.assignmentReassignSelected,
      true,
    );

  const handleShowExpirationDateClick = () => {
    if (codeInfo) {
      setIsAssignmentActionsDropdownOpen(false);
      setIsExpirationDateVisible((prev) => !prev);

      onShowExpirationDateClick?.(codeInfo.expirationTS);

      return;
    }

    // reset error message before each request to apply correct styles to notification popup
    dispatch(setAssignmentActionErrorMessage(null));

    fetchCodeInfo(classCode)
      .unwrap()
      .then((codeInfo) => {
        setIsExpirationDateVisible(true);

        onShowExpirationDateClick?.(codeInfo.expirationTS);
      })
      .catch(() => {
        dispatch(setAssignmentActionErrorMessage(CODE_INFO_ERROR_MESSAGE));
        dispatch(setIsAssignmentActionNotificationActive(true));
      })
      .finally(() => {
        setIsAssignmentActionsDropdownOpen(false);
      });
  };

  return (
    <div
      className={styles.assignmentActionsMenu}
      ref={assignmentActionsMenuRef}
      data-testid="assignment-actions-menu"
    >
      <Tooltip
        title={t('More')}
        tooltipClassNames={styles.actionsMenuButtonTooltip}
        showOnTouch={false}
      >
        <button
          type="button"
          className="btn btn-icon-ghost rhythm-button"
          onClick={handleAssignmentBtnClick}
          aria-label="More"
          data-testid="assignment-actions-btn"
        >
          <i className="icon-v2-overflow-horizontal" aria-hidden />
        </button>
      </Tooltip>
      <CSSTransition
        nodeRef={assignmentActionsDropdownRef}
        in={isAssignmentActionsDropdownOpen}
        timeout={ROTATE_X_ANIMATION_DURATION}
        classNames="rotateX"
        unmountOnExit
      >
        <div
          ref={assignmentActionsDropdownRef}
          className={styles.assignmentActionsMenuOptions}
        >
          {!isAssignmentUnassigned && !isSingleSessionCodeAssignment && (
            <AssignmentActionsMenuOption
              optionName={t('Edit')}
              onClick={handleEditClick}
              onContextMenu={handleEditContextMenu}
            />
          )}
          {!isAssignmentUnassigned && !isSingleSessionCodeAssignment && (
            <AssignmentActionsMenuOption
              optionName={t('Unassign')}
              onClick={handleUnassignClick}
              onContextMenu={handleUnassignContextMenu}
            />
          )}
          {isAssignmentUnassigned && !isSingleSessionCodeAssignment && (
            <AssignmentActionsMenuOption
              optionName={t('Reassign')}
              onClick={handleReassignClick}
              onContextMenu={handleReassignContextMenu}
            />
          )}
          {isSingleSessionCodeAssignment && (
            <AssignmentActionsMenuOption
              optionName={expirationDateOptionName}
              onClick={handleShowExpirationDateClick}
              withLoadingSpinner={isCodeInfoLoading}
            />
          )}
        </div>
      </CSSTransition>
    </div>
  );
};
